import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { getEnv } from 'mobx-state-tree';
import { reaction } from 'mobx';
import { Observer } from 'mobx-react';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import proceduresIcon from '@assets/procedures-icon.svg'
import DescriptionIcon from '@mui/icons-material/Description';
import EditIcon from '@assets/edit.svg';
import NeoIcon from '@assets/competencies-icon.svg';
import { DeviceIcon } from '@shared/components/DeviceIcon';

import { Table } from '@shared/components/Table';
import { NoResultsView } from '@shared/components/NoResultsView';

import {
    useUserPermissionsUIStore,
    useProceduresUIStore,
    useProcedureUIStore,
    useGroupsUIStore
} from '@core/useStores';
import { IStoresEnv } from '@core/storesEnv';
import { RoutePaths } from '@core/routes/RoutePaths';
import { useNavigate } from 'react-router-dom';
import { useStyles, getCustomStyles } from './ProceduresList.styles'

import { SearchForm } from '@shared/components/SearchForm';
import { ExecutionModes } from '@shared/components/ExecutionModes';

import { messages } from 'Procedures/procedures.messages';
import { PROCEDURES_IN_GROUP_ID } from 'Procedures/domain/constants';
import {
    IPageQueryStoreSnapshotIn,
} from 'Users/stores';
import { IProcedureStore } from 'Procedures/stores/Procedure.store';
import { ThemeProps } from '@styles/theme';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { NoProceduresInGroup } from '../../../Procedures/components/NoProceduresInGroup';
import { Checkbox, Tooltip } from '@mui/material';
import Select from 'react-select';
import { ProcedureVersionAssignment } from 'Procedures/services';
import { ProcedureModal } from 'Procedures/components/ProcedureModal';
import { VersionOptions, VersionOptionType, VersionValue } from 'Users/components/ProcedureAssignmentDropdown/ProcedureAssignmentDropdown';

type FetchDataType = {
    pageIndex: number;
    sortBy: {
        id: string;
        desc: boolean;
    }[];
};

type ProcedureMedia = {
    src: string;
    description: string;
}

const PROCEDURE_TYPE_LABELS: Record<string, string> = {
    VT: 'Virtual Training',
    OpS: 'Operating Scenario',
};

const GROUP_PROCEDURES_LIMIT = 100;

type GroupProceduresCtxType = {
    procedureIds: string[];
    onToggle: (procedure: IProcedureStore, add: boolean) => void;
    onSelectAll: () => void;
    onDeselectAll: () => void;
};

const GroupProceduresCtx = React.createContext<GroupProceduresCtxType | null>(null);

const NoInfo = () => {
    const styles = useGridStyles();

    return <div className={styles.noInfo}>{messages['proceduresPage.noInfo']}</div>;
};

const getProcedureTypeLabel = (value: string | null) => {
    if (!value) {
        return '';
    }

    return PROCEDURE_TYPE_LABELS[value] || value;
};

const columnHelper = createColumnHelper<IProcedureStore>();

const columnsData = [
    columnHelper.accessor('groups', {
        id: 'id',
        meta: { cardRole: 'selection' },
        header: () => {
            const ctx = React.useContext(GroupProceduresCtx);
            const proceduresGridUIStore = useProceduresUIStore();
            const groupsUIStore = useGroupsUIStore();
            if (!ctx || !groupsUIStore.massEdit) {return <></>;}

            return (
                <Observer>
                    {() => {
                        const total = proceduresGridUIStore.procedures.length;
                        const selectedCount = ctx.procedureIds.length;
                        const allSelected = selectedCount === total && total > 0;
                        const someSelected = selectedCount > 0 && !allSelected;

                        return (
                            <Checkbox
                                indeterminate={someSelected}
                                checked={allSelected}
                                onClick={() => {
                                    if (allSelected) {ctx.onDeselectAll();} else {ctx.onSelectAll();}
                                }}
                            />
                        );
                    }}
                </Observer>
            );
        },
        minSize: 40,
        maxSize: 50,
        size: 5,
        enableSorting: false,
        cell: ({ cell: {row}}) => {
            const styles = useGridStyles();
            const procedure = row.original;
            const ctx = React.useContext(GroupProceduresCtx);
            const checked = ctx?.procedureIds.includes(procedure.id) ?? false;

            return (
                <div className={styles.nameWrapper}>
                    <Checkbox
                        checked={checked}
                        onClick={() => ctx?.onToggle(procedure, !checked)}
                    />
                </div>
            );
        },
    }),
    columnHelper.accessor('name', {
        header: messages['proceduresPage.table.header.name'],
        meta: { cardRole: 'title' },
        size: 34,
        cell: ({ cell: {row}, getValue }) => {
            const styles = useGridStyles();
            const { procedurePreview } = row.original as {
                procedurePreview: ProcedureMedia
            };
            const media = procedurePreview;

            return (
                <div className={styles.nameWrapper}>
                    {
                        media
                            ? (
                                <img
                                    className={styles.procedureImage}
                                    src={media.src}
                                    alt={media.description}
                                />
                            )
                            : (
                                <div className={styles.procedureImage}>
                                    <DescriptionIcon color='primary'/>
                                </div>
                            )
                    }
                    <span className={styles.procedureName}>{getValue()}</span>
                </div>
            );
        },
    }),
    columnHelper.accessor('availableModes', {
        header: messages['proceduresPage.table.header.availableModes'],
        meta: { cardRole: 'chip' },
        size: 16,
        cell: ({ getValue }) => {
            const value = getValue();
            if (!value.length) {
                return <NoInfo />;
            }

            return (
                <ExecutionModes modes={value} size='small' />
            );
        },
    }),
    columnHelper.accessor('type', {
        header: messages['proceduresPage.table.header.type'],
        meta: { cardRole: 'chip' },
        size: 10,
        cell: ({ getValue }) => {
            const styles = useGridStyles();
            const value = getValue();

            if (!value) {
                return <NoInfo />;
            }

            return (
                <Tooltip title={getProcedureTypeLabel(value)} arrow>
                    <span className={styles.typeBadge}>{value}</span>
                </Tooltip>
            );
        },
    }),
    columnHelper.accessor('versions', {
        header: 'Version',
        size: 24,
        meta: {
            cardRole: 'detail',
            disableEllipsis: true,
        },
        cell: ({ cell: {row} }) => {
            const styles = useGridStyles();
            const versionSelectStyles = useVersionSelectStyles();

            const procedureUIStore = useProcedureUIStore();
            const proceduresUIStore = useProceduresUIStore();
            const groupsUIStore = useGroupsUIStore();

            const procedure = row.original;

            const procedureGroups = procedure.groups;
            const group = procedureGroups.find((u: any) => u.id == groupsUIStore.selectedGroup?.id);
            const assignedVersion = group?.data?.assignedVersion ? group.data?.assignedVersion : null;

            const verOpt: VersionOptions[] = [];
            proceduresUIStore.procedures.forEach(p => {
                if(p){
                    const versions = p.versions;
                    const option: VersionOptionType[] = versions.map(v => ({
                        label: v.version,
                        value: v.id,
                        devices: v.devices.map((d: any) => ({ platform: d.platform, environments: Array.from(d.environments) })),
                        date: v.date,
                        languages: Array.from(v.languages),
                        editorVersion: v.editorVersion ?? undefined,
                    }));
                    option.unshift({value: null, label: 'latest'});
                    verOpt.push({procedureId: p.id, options: option});
                }
            })

            const versionOptions = verOpt.find(p => p.procedureId == row.original.id);
            const version = versionOptions?.options.find(v => v.value == assignedVersion);

            const options = versionOptions?.options.map((o: any) => ({
                value: { versionId: o.value, procedureId: procedure.id },
                label: o.label,
                disabled: o.disabled,
                devices: o.devices,
                date: o.date,
                languages: o.languages,
                editorVersion: o.editorVersion,
            }));

            const formatVersionOptionLabel = (option: any, { context }: { context: string }) => {
                if (context === 'value') {
                    return <span>{option.label}</span>;
                }
                const dateStr = option.date
                    ? new Date(option.date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
                    : null;
                const hasExtra = dateStr || option.languages?.length || option.devices?.length || option.editorVersion;

                return (
                    <div className={styles.versionOption}>
                        <span className={styles.versionOptionLabel}>{option.label}</span>
                        {hasExtra && (
                            <div className={styles.versionMeta}>
                                {dateStr && <span className={styles.versionDate}>{dateStr}</span>}
                                {option.editorVersion && (
                                    <span className={styles.versionMetaChip}>{option.editorVersion}</span>
                                )}
                                {option.languages?.length > 0 && option.languages.map((lang: string, i: number) => (
                                    <span key={i} className={styles.versionMetaChip}>{lang.toUpperCase()}</span>
                                ))}
                                {option.devices?.length > 0 && option.devices.map((d: any, i: number) => d.platform && (
                                    <DeviceIcon
                                        key={i}
                                        platform={d.platform}
                                        environments={d.environments?.join('|') || null}
                                        size="small"
                                        showLabel={false}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                );
            };

            const versionValue: VersionValue = {value: { versionId: version?.value ? version?.value : null, procedureId: procedure.id}, label: version?.label ? version?.label : 'latest', disabled: false};

            const handleChange = async (selectValue: any) => {
                const assignments: Array<ProcedureVersionAssignment> = [];

                proceduresUIStore.procedures.forEach(p => {
                    const groups = p.groups;
                    const groupsIds = groups.map(g => g.id);

                    const users = p.users;
                    const usersIds = users.map(g => g.id);

                    if(p.id == procedure.id){
                        p.versions.forEach(version => {
                            const versionGroupsIds = groups.filter(g => g.data?.assignedVersion == version.id).map((g: any) => g.id);
                            const versionUsersIds = users.filter(g => g.data?.assignedVersion == version.id).map((g: any) => g.id);

                            const assignedToVersion = groups.find(u => u.data?.assignedVersion == version.id && u.id == group?.id);

                            if((version.id != selectValue.value.versionId) && assignedToVersion){
                                const groupToRemove = versionGroupsIds.find(id => id == group?.id);
                                const groupIdx = versionUsersIds.indexOf(groupToRemove);
                                versionGroupsIds.splice(groupIdx, 1);
                            }else if((version.id == selectValue.value.versionId) && !assignedToVersion){
                                versionGroupsIds.push(group?.id);
                            }

                            assignments.push({
                                id: p.id,
                                versionId: version.id,
                                userIds: versionUsersIds.length == 0 ? [] : usersIds,
                                groupIds: versionGroupsIds.length == 0 ? [] : groupsIds,
                            });

                        });

                        const versionGroupsIds = groups.filter(g => g.data?.assignedVersion == null).map((g: any) => g.id);
                        const versionUsersIds = users.filter(g => g.data?.assignedVersion == null).map((g: any) => g.id);

                        const assignedToVersion = groups.find(u => u.data?.assignedVersion == null && u.id == group?.id);

                        if((selectValue.value.versionId != null) && assignedToVersion){
                            const groupToRemove = versionGroupsIds.find(id => id == group?.id);
                            const groupIdx = versionUsersIds.indexOf(groupToRemove);
                            versionGroupsIds.splice(groupIdx, 1);
                        }else if((selectValue.value.versionId == null) && !assignedToVersion){
                            versionGroupsIds.push(group?.id);
                        }

                        assignments.push({
                            id: p.id,
                            versionId: null,
                            userIds: versionUsersIds.length == 0 ? [] : usersIds,
                            groupIds: versionGroupsIds.length == 0 ? [] : groupsIds,
                        });
                    }else{
                        p.versions.forEach(version => {
                            const versionGroupsIds = groups.filter(g => g.data?.assignedVersion == version.id).map((g: any) => g.id);
                            const versionUsersIds = users.filter(g => g.data?.assignedVersion == version.id).map((g: any) => g.id);

                            assignments.push({
                                id: p.id,
                                versionId: version.id,
                                userIds: versionUsersIds.length == 0 ? [] : usersIds,
                                groupIds: versionGroupsIds.length == 0 ? [] : groupsIds,
                            });

                        });

                        const versionGroupsIds = groups.filter(g => g.data?.assignedVersion == null).map((g: any) => g.id);
                        const versionUsersIds = users.filter(g => g.data?.assignedVersion == null).map((g: any) => g.id);

                        assignments.push({
                            id: p.id,
                            versionId: null,
                            userIds: versionUsersIds.length == 0 ? [] : usersIds,
                            groupIds: versionGroupsIds.length == 0 ? [] : groupsIds,
                        });
                    }

                });
                await procedureUIStore.saveVersionAssignments(assignments);
            }

            return (
                <div className={styles.versionSelectWrapper}>
                    <Select
                        className={styles.select}
                        classNamePrefix='group-procedure-version'
                        isDisabled={!groupsUIStore.selectedGroup}
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                        menuPosition='fixed'
                        menuPlacement='auto'
                        menuShouldScrollIntoView={false}
                        onChange={handleChange}
                        styles={versionSelectStyles}
                        defaultValue={versionValue}
                        isMulti={false}
                        options={options}
                        formatOptionLabel={formatVersionOptionLabel}
                    />
                </div>
            );
        },
    }),
    columnHelper.display({
        id: 'actions',
        meta: { cardRole: 'actions' },
        header: () => {
            return <></>;
        },
        minSize: 80,
        maxSize: 100,
        size: 10,
        enableSorting: false,
        cell: ({ cell: { row } }) => {
            const styles = useGridStyles();

            const procedureUIStore = useProcedureUIStore();
            const userPermissionsUIStore = useUserPermissionsUIStore();
            const original: IProcedureStore = row.original;

            const onCellClick = () => {
                procedureUIStore.setEditProcedureId(original.id);
                procedureUIStore.toggleProcedureModalOpen(true);
            }
            const navigate = useNavigate();
            const defaultProcedure = original.companyId == "00000000-0000-0000-0000-000000000000" && !userPermissionsUIStore.isCompaniesAvailable;

            return (
                <div className={styles.actionsWrapper}>
                    <button
                        type="button"
                        className={styles.button}
                        title="View procedure analytics"
                        onClick={() => navigate(`/${RoutePaths.procedures}/analytics/${original.id}`)}
                    >
                        <img src={NeoIcon} style={{ width: 22, height: 22 }} />
                    </button>
                    <button
                        onClick={() => (userPermissionsUIStore.canUpdateProcedure || defaultProcedure) && onCellClick()}
                        disabled={defaultProcedure || !userPermissionsUIStore.canUpdateProcedure}
                        className={styles.button}
                        title={messages['proceduresPage.editProcedure']}
                    >
                        <img src={EditIcon} />
                    </button>
                </div>
            )
        },
    }),
];

const useGridStyles = () => {
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });

    return styles;
};

const useVersionSelectStyles = () => {
    const theme = useTheme<ThemeProps>();

    return getCustomStyles(theme.colors);
};

export const ProceduresList: React.FC = () => {
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });

    const proceduresGridUIStore = useProceduresUIStore();
    const procedureUIStore = useProcedureUIStore();
    const groupsUIStore = useGroupsUIStore();
    const { filtersOptions, groups } = getEnv<IStoresEnv>(proceduresGridUIStore);

    const [procedureIds, setProcedureIds] = useState<string[]>(() => {
        if (groupsUIStore.massEdit && groups.selectedGroup) {
            return proceduresGridUIStore.procedures
                .filter(p => p.groups.some(g => g.id === groups.selectedGroup?.id))
                .map(p => p.id);
        }

        return [];
    });

    const procedureIdsRef = useRef(procedureIds);
    procedureIdsRef.current = procedureIds;

    useEffect(() => {
        const disposer = reaction(
            () => groupsUIStore.massEdit,
            massEdit => {
                if (massEdit) {
                    const ids = proceduresGridUIStore.procedures
                        .filter(p => p.groups.some(g => g.id === groups.selectedGroup?.id))
                        .map(p => p.id);
                    setProcedureIds(ids);
                } else {
                    setProcedureIds([]);
                }
            },
        );

        return () => disposer();
    }, []);

    const onProcedureToggle = useCallback(async (procedure: IProcedureStore, add: boolean) => {
        const procId = procedure.id;
        setProcedureIds(prev => add ? [...prev, procId] : prev.filter(id => id !== procId));
        let groupIds = procedure.groups.map(g => g.id);
        if (add) {
            if (groups.selectedGroup?.id) {groupIds.push(groups.selectedGroup.id);}
        } else {
            groupIds = groupIds.filter(id => id !== groups.selectedGroup?.id);
        }
        await procedureUIStore.saveVersionAssignments([{
            id: procedure.id,
            versionId: null,
            userIds: procedure.users?.map(u => u.id),
            groupIds,
        }]);
    }, [groups, procedureUIStore]);

    const onProcedureSelectAll = useCallback(async () => {
        const notInGroup = proceduresGridUIStore.procedures.filter(p => !procedureIdsRef.current.includes(p.id));
        setProcedureIds(proceduresGridUIStore.procedures.map(p => p.id));
        await Promise.all(
            notInGroup.map(procedure => {
                const groupIds = [...procedure.groups.map(g => g.id)];
                if (groups.selectedGroup?.id) {groupIds.push(groups.selectedGroup.id);}

                return procedureUIStore.saveVersionAssignments([{
                    id: procedure.id,
                    versionId: null,
                    userIds: procedure.users?.map(u => u.id),
                    groupIds,
                }]);
            })
        );
    }, [proceduresGridUIStore, groups, procedureUIStore]);

    const onProcedureDeselectAll = useCallback(async () => {
        const inGroup = proceduresGridUIStore.procedures.filter(p => procedureIdsRef.current.includes(p.id));
        setProcedureIds([]);
        await Promise.all(
            inGroup.map(procedure => {
                const groupIds = procedure.groups.map(g => g.id).filter(id => id !== groups.selectedGroup?.id);

                return procedureUIStore.saveVersionAssignments([{
                    id: procedure.id,
                    versionId: null,
                    userIds: procedure.users?.map(u => u.id),
                    groupIds,
                }]);
            })
        );
    }, [proceduresGridUIStore, groups, procedureUIStore]);

    const proceduresCtxValue = useMemo(() => ({
        procedureIds,
        onToggle: onProcedureToggle,
        onSelectAll: onProcedureSelectAll,
        onDeselectAll: onProcedureDeselectAll,
    }), [procedureIds, onProcedureToggle, onProcedureSelectAll, onProcedureDeselectAll]);

    const onSearchTextChanged = (searchText: string) => {
        if (searchText && searchText.length === 1) {
            return;
        }

        proceduresGridUIStore.setParams({
            page: 0,
            searchText,
        });
    };

    const initialSortState = {
        sorting: [
            {
                id: 'name',
                desc: false,
            },
        ],
    };

    const columns: ColumnDef<IProcedureStore, any>[] = React.useMemo(() => {
        return columnsData.filter((column: any) => !['type'].includes(column.id ?? column.accessorKey));
    }, []);

    useEffect(() => {
        proceduresGridUIStore.cleanUp();

        proceduresGridUIStore.setParams({
            page: 0,
            limit: GROUP_PROCEDURES_LIMIT,
            inGroup: PROCEDURES_IN_GROUP_ID,
            inGroupId: groups.selectedGroup!.id,
        });

        proceduresGridUIStore.togglePageActivity(true);
        proceduresGridUIStore.load();
        proceduresGridUIStore.loadStatistics();
        filtersOptions.loadFilterOptions();

        return () => {
            proceduresGridUIStore.togglePageActivity(false);
            proceduresGridUIStore.cleanUp();
        }
    }, []);

    const onFetchData = ({ pageIndex, sortBy }: FetchDataType) => {
        const params: IPageQueryStoreSnapshotIn = { page: pageIndex };

        if (sortBy && sortBy.length) {
            params.sortFieldName = sortBy[0].id;
            params.sortDirection = Number(sortBy[0].desc);
        }
        if (proceduresGridUIStore.isActivePage) {
            proceduresGridUIStore.setParams(params);
        }
    };

    const closeProcedureModal = () => {
        procedureUIStore.toggleProcedureModalOpen(false);
        procedureUIStore.cleanUpProcedureModal();
    };

    return (
        <GroupProceduresCtx.Provider value={proceduresCtxValue}>
            <Observer>
                {() => {
                    const data = proceduresGridUIStore.procedures.toJSON();
                    const preparedData = data.map(row => ({
                        ...row,
                    }));

                    let genericColumns = columns as ColumnDef<{}, any>[];

                    if(!groupsUIStore.massEdit){
                        const checkboxColumn = genericColumns[0];
                        genericColumns = genericColumns.filter(c => c != checkboxColumn) as ColumnDef<{}, any>[];
                    }

                    const selectedGroupProcedures = groupsUIStore.massEdit
                        ? procedureIds.length
                        : proceduresGridUIStore.procedures.filter(p => p.groups.find(g => g.id == groups.selectedGroup?.id)).length;

                    return (
                        <>
                            {
                                procedureUIStore.procedureModalOpened && (
                                    <ProcedureModal
                                        isOpen={true}
                                        onRequestClose={() => closeProcedureModal()}
                                    />
                                )
                            }
                            <div className={styles.mainWrapper}>
                                <div className={styles.headerWrapper}>
                                    <div className={styles.titleWrapper}>
                                        <Typography variant="h6" className={styles.sectionTitle}>
                                        Procedures
                                        </Typography>
                                        <div className={styles.assignedCounter}>
                                            <img src={proceduresIcon} />
                                            <span className={styles.moreItemsPresent}>
                                                {selectedGroupProcedures} assigned procedures
                                            </span>
                                        </div>
                                    </div>
                                    <SearchForm
                                        initialValues={{
                                            search: proceduresGridUIStore.requestParams.getStorageParams
                                                ? proceduresGridUIStore.requestParams.getStorageParams.searchText
                                                : '',
                                        }}
                                        onSearchTextChanged={onSearchTextChanged}
                                        className={styles.searchPanel}
                                    />
                                </div>
                                {groups.selectedGroup && (
                                    <div className={styles.tableWrapper}>
                                        {proceduresGridUIStore.isTableVisible && (
                                            <Table
                                                data={preparedData}
                                                columns={genericColumns}
                                                fetchData={onFetchData}
                                                isLoading={
                                                    proceduresGridUIStore.status.isLoading
                                                }
                                                useSkeleton={true}
                                                paginationStatus={{
                                                    ...proceduresGridUIStore.pagination,
                                                }}
                                                paginationDisabled
                                                initialState={initialSortState}
                                            // isAlt={true}
                                            />
                                        )}
                                    </div>
                                )
                                }

                                {proceduresGridUIStore.noFilteringResults && (
                                    <NoResultsView entityName="procedures" />
                                )}

                                {proceduresGridUIStore.noItemsInGroup && (
                                    <NoProceduresInGroup />
                                )}
                            </div>
                        </>
                    );
                }}
            </Observer>
        </GroupProceduresCtx.Provider>
    );
};
